// components/entities/UserRenderer.tsx
import { memo } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { BaseEntityProps, EntityRowAction, LeasingUserDTO } from '#types/types';
import { User } from '@/components/ui/lucide-memo';
import UserAvatar from '@/components/UserAvatar';
import clsx from 'clsx';
import { portalTableRow } from '../portal/portalTheme';
import { useTableGlass, TABLE_GLASS_ROW } from '../portal/tableSurface';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';

const EMPTY_ROW_ACTIONS: EntityRowAction<LeasingUserDTO>[] = [];

export const UserRenderer = memo(({
  entityId,
  variant = 'row',
  onClick,
  className,
  rowActions = EMPTY_ROW_ACTIONS,
}: BaseEntityProps<LeasingUserDTO>) => {
  const data = useKernelStore(state => state.inventory.User.registry[entityId]) as LeasingUserDTO | undefined;
  const glass = useTableGlass();

  if (!data) return <EntitySkeleton variant={variant} className={className} />;

  if (variant === 'chip') {
    return (
      <div className="flex items-center gap-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs">
        <User size={12} /> {data.firstName} {data.lastName}
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={clsx(
        portalTableRow,
        'group grid grid-cols-[50px_1fr_1fr_auto] gap-4 px-6 items-center py-3 w-full',
        glass && TABLE_GLASS_ROW,
        className
      )}
    >
      <UserAvatar
        userId={data.id}
        name={[data.firstName, data.lastName].filter(Boolean).join(' ').trim() || data.email}
        canEdit={false}
        isOrganisationAdmin={false}
        size={32}
        shape="circle"
      />
      <div className="text-sm text-white font-medium">{data.firstName} {data.lastName}</div>
      <div className="text-xs text-white/50">{data.email}</div>
      <div className="flex items-center justify-end">
        <EntityRowActions entityType="User" entity={data} rowActions={rowActions} />
      </div>
    </div>
  );
});

UserRenderer.displayName = 'UserRenderer';
